import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { 
  Search, 
  Sparkles, 
  User, 
  Truck, 
  Eye,
  Filter,
  X,
  Clock,
  TrendingUp,
  Camera,
  MapPin,
  Play,
  ArrowRight,
  ArrowLeft,
  Calendar,
  Users,
  Video
} from 'lucide-react';
import { eventService } from '../../services';
import { useLocation } from '../../contexts/LocationContext';
import RegisteredPeopleLinks from '../../components/events/RegisteredPeopleLinks';
import type { 
  AISearchSuggestions, 
  UnifiedSearchResponse, 
  UnifiedSearchFilters,
  AIMetadata,
  EventResult, 
  VisitorLogResult 
} from '../../types';
import { formatEventDate, formatEventTime } from '../../utils/eventTime';

type SearchResult = EventResult | VisitorLogResult;

const normalizeAIMetadata = (value: AIMetadata | string | null | undefined): AIMetadata | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as AIMetadata;
    } catch {
      return null;
    }
  }
  return value;
};

const AISearchPage: React.FC = () => {
  const navigate = useNavigate();
  const { locations, selectedLocationId } = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [activeSearch, setActiveSearch] = useState<UnifiedSearchFilters | null>(null);
  const requestRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchResponse, setSearchResponse] = useState<UnifiedSearchResponse | null>(null);
  const [suggestions, setSuggestions] = useState<AISearchSuggestions | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{ type: 'video' | 'image'; url: string } | null>(null);

  // Filter states - initialize with current location from context
  const [includeEvents, setIncludeEvents] = useState(true);
  const [includeVisitorLogs, setIncludeVisitorLogs] = useState(true);
  const [filterLocation, setFilterLocation] = useState(selectedLocationId);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Person filter states (for old events-only search)
  const [personFilters, setPersonFilters] = useState({
    gender: '',
    clothing_color: '',
    clothing_type: '',
  });
  const [vehicleFilters, setVehicleFilters] = useState({
    vehicle_type: '',
    vehicle_color: '',
    unit_number: '',
    license_plate: '',
  });

  // Load suggestions on mount
  useEffect(() => {
    loadSuggestions();
    return () => { requestRef.current += 1; abortRef.current?.abort(); };
  }, []);

  // Sync filter location with context when it changes
  useEffect(() => {
    setFilterLocation(selectedLocationId);
    requestRef.current += 1;
    abortRef.current?.abort();
    setResults([]); setSearchResponse(null); setActiveSearch(null);
    setIsLoading(false); setIsLoadingMore(false);
  }, [selectedLocationId]);

  const loadSuggestions = async () => {
    try {
      const data = await eventService.getSearchSuggestions();
      setSuggestions(data);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    }
  };

  const handleSearch = useCallback(async (query?: string) => {
    const searchText = query ?? searchQuery;
    if (!searchText.trim() || searchText.trim().length < 2) {
      setError('Please enter at least 2 characters to search');
      return;
    }

    setIsLoading(true);
    setIsLoadingMore(false);
    setError(null);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const request = ++requestRef.current;

    try {
      // Always pass the selected location (required by backend for scoped search)
      const locationId = filterLocation !== 'all' ? parseInt(filterLocation) : undefined;
      
      const filters: UnifiedSearchFilters = {
        q: searchText,
        location_id: locationId,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit: 50,
        offset: 0,
        include_events: includeEvents,
        include_visitor_logs: includeVisitorLogs,
      };
      const response = await eventService.unifiedSearch(filters, controller.signal);
      if (request !== requestRef.current) return;
      setActiveSearch(filters);
      setSearchResponse(response);
      setResults(response.results || []);
    } catch (err) {
      if (request !== requestRef.current || controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Search failed');
      setResults([]);
      setSearchResponse(null);
    } finally {
      if (request === requestRef.current) setIsLoading(false);
    }
  }, [searchQuery, filterLocation, startDate, endDate, includeEvents, includeVisitorLogs]);

  const loadMore = async () => {
    if (!activeSearch || !searchResponse?.has_more || isLoadingMore) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const request = ++requestRef.current;
    setIsLoadingMore(true); setError(null);
    const offset = (searchResponse.offset ?? 0) + searchResponse.limit;
    try {
      const response = await eventService.unifiedSearch({ ...activeSearch, offset }, controller.signal);
      if (request !== requestRef.current) return;
      setResults(previous => {
        const seen = new Set(previous.map(result => result.result_type === 'event' ? `e${result.event_id}` : `v${result.log_id}`));
        return [...previous, ...(response.results || []).filter(result => !seen.has(
          result.result_type === 'event' ? `e${result.event_id}` : `v${result.log_id}`))];
      });
      setSearchResponse(response);
    } catch (err) {
      if (request === requestRef.current && !controller.signal.aborted) setError(err instanceof Error ? err.message : 'Could not load more results');
    } finally {
      if (request === requestRef.current) setIsLoadingMore(false);
    }
  };

  const handlePersonSearch = async () => {
    const hasFilters = Object.values(personFilters).some(v => v);
    if (!hasFilters) return;
    requestRef.current += 1;
    abortRef.current?.abort();
    setActiveSearch(null); setIsLoadingMore(false);

    setIsLoading(true);
    setError(null);

    try {
      const response = await eventService.searchByPerson({
        gender: personFilters.gender as 'male' | 'female' | undefined,
        clothing_color: personFilters.clothing_color || undefined,
        clothing_type: personFilters.clothing_type || undefined,
      });
      // Convert old format to unified format
      const eventResults: EventResult[] = (response.results || []).map(event => ({
        result_type: 'event' as const,
        event_id: event.event_id,
        event_code: event.event_code,
        event_type: event.event_type,
        event_tag: event.event_tag,
        created_date: event.created_date || '',
        camera_id: event.event_camera,
        location_id: event.event_location,
        snapshot_url: event.event_snapshot_url || null,
        video_url: event.event_video_url || null,
        ai_description: event.ai_description || null,
        ai_metadata: normalizeAIMetadata(event.ai_metadata),
        match_score: event.match_score || 0,
        camera_name: event.camera_name,
        location_name: event.location_name,
      }));
      setResults(eventResults);
      setSearchResponse({
        success: true,
        query: 'Person search',
        keywords: [],
        date_filter: null,
        date_range: null,
        results: eventResults,
        total: response.total || 0,
        events_count: response.total || 0,
        visitor_logs_count: 0,
        limit: 50,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVehicleSearch = async () => {
    const hasFilters = Object.values(vehicleFilters).some(v => v);
    if (!hasFilters) return;
    requestRef.current += 1;
    abortRef.current?.abort();
    setActiveSearch(null); setIsLoadingMore(false);

    setIsLoading(true);
    setError(null);

    try {
      const response = await eventService.searchByVehicle({
        vehicle_type: vehicleFilters.vehicle_type || undefined,
        vehicle_color: vehicleFilters.vehicle_color || undefined,
        unit_number: vehicleFilters.unit_number || undefined,
        license_plate: vehicleFilters.license_plate || undefined,
      });
      // Convert old format to unified format
      const eventResults: EventResult[] = (response.results || []).map(event => ({
        result_type: 'event' as const,
        event_id: event.event_id,
        event_code: event.event_code,
        event_type: event.event_type,
        event_tag: event.event_tag,
        created_date: event.created_date || '',
        camera_id: event.event_camera,
        location_id: event.event_location,
        snapshot_url: event.event_snapshot_url || null,
        video_url: event.event_video_url || null,
        ai_description: event.ai_description || null,
        ai_metadata: normalizeAIMetadata(event.ai_metadata),
        match_score: event.match_score || 0,
        camera_name: event.camera_name,
        location_name: event.location_name,
      }));
      setResults(eventResults);
      setSearchResponse({
        success: true,
        query: 'Vehicle search',
        keywords: [],
        date_filter: null,
        date_range: null,
        results: eventResults,
        total: response.total || 0,
        events_count: response.total || 0,
        visitor_logs_count: 0,
        limit: 50,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    handleSearch(suggestion);
  };

  const clearFilters = () => {
    setPersonFilters({ gender: '', clothing_color: '', clothing_type: '' });
    setVehicleFilters({ vehicle_type: '', vehicle_color: '', unit_number: '', license_plate: '' });
    setFilterLocation(selectedLocationId); // Reset to current context location
    setStartDate('');
    setEndDate('');
    setIncludeEvents(true);
    setIncludeVisitorLogs(true);
  };

  const clearResults = () => {
    requestRef.current += 1;
    abortRef.current?.abort();
    setIsLoading(false); setIsLoadingMore(false); setActiveSearch(null);
    setResults([]);
    setSearchQuery('');
    setSearchResponse(null);
    clearFilters();
  };

  const incidentZone = (locationId?: number) => locations.find(location => location.ycl_id === locationId)?.location_timezone;
  const formatDate = (date?: string, locationId?: number) => formatEventDate(date, { timeZone: incidentZone(locationId) });
  const formatTime = (date?: string, locationId?: number) => formatEventTime(date, { timeZone: incidentZone(locationId) });

  const getMatchScoreBadge = (score?: number) => {
    if (!score) return null;
    const percentage = Math.round(score * 100);
    const variant = percentage >= 80 ? 'success' : percentage >= 50 ? 'warning' : 'danger';
    return (
      <Badge variant={variant} size="sm">
        {percentage}% match
      </Badge>
    );
  };

  const isEventResult = (result: SearchResult): result is EventResult => {
    return result.result_type === 'event';
  };

  const isVisitorLogResult = (result: SearchResult): result is VisitorLogResult => {
    return result.result_type === 'visitor_log';
  };

  const genderOptions = [
    { label: 'Any Gender', value: '' },
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
  ];

  const clothingColorOptions = [
    { label: 'Any Color', value: '' },
    { label: 'Red', value: 'red' },
    { label: 'Blue', value: 'blue' },
    { label: 'Green', value: 'green' },
    { label: 'Yellow', value: 'yellow' },
    { label: 'Orange', value: 'orange' },
    { label: 'Black', value: 'black' },
    { label: 'White', value: 'white' },
    { label: 'Gray', value: 'gray' },
  ];

  const clothingTypeOptions = [
    { label: 'Any Type', value: '' },
    { label: 'Jacket', value: 'jacket' },
    { label: 'Vest', value: 'vest' },
    { label: 'Shirt', value: 'shirt' },
    { label: 'Uniform', value: 'uniform' },
    { label: 'Hard Hat', value: 'hard_hat' },
  ];

  const vehicleTypeOptions = [
    { label: 'Any Type', value: '' },
    { label: 'Truck', value: 'truck' },
    { label: 'Van', value: 'van' },
    { label: 'Pickup', value: 'pickup' },
    { label: 'Car', value: 'car' },
    { label: 'Forklift', value: 'forklift' },
  ];

  const vehicleColorOptions = [
    { label: 'Any Color', value: '' },
    { label: 'Red', value: 'red' },
    { label: 'Blue', value: 'blue' },
    { label: 'White', value: 'white' },
    { label: 'Black', value: 'black' },
    { label: 'Yellow', value: 'yellow' },
    { label: 'Green', value: 'green' },
    { label: 'Gray', value: 'gray' },
    { label: 'Orange', value: 'orange' },
  ];

  const locationOptions = [
    { label: 'All Locations', value: 'all' },
    ...locations.map(loc => ({ label: loc.ycl_name, value: String(loc.ycl_id) })),
  ];

  // Get current location name for display
  const currentLocationName = filterLocation === 'all' 
    ? 'All Locations' 
    : locations.find(l => String(l.ycl_id) === filterLocation)?.ycl_name || 'All Locations';

  // Event Card Component
  const EventCard: React.FC<{ event: EventResult }> = ({ event }) => {
    const [imageError, setImageError] = React.useState(false);
    
    return (
      <Card padding="none" className="overflow-hidden hover:shadow-lg transition-shadow">
        {/* Header Badge */}
        <div className="bg-blue-500/10 px-4 py-2 flex items-center justify-between border-b border-surface-tertiary">
          <div className="flex items-center gap-2">
            <Video size={16} className="text-blue-500" />
            <span className="text-sm font-medium text-blue-500">CAMERA EVENT</span>
          </div>
          <span className="text-xs text-text-muted">{formatTime(event.created_date, event.location_id)}</span>
        </div>

        {/* Thumbnail */}
        <div 
          className="relative aspect-video bg-surface-secondary cursor-pointer group"
          onClick={() => event.snapshot_url && !imageError && setSelectedMedia({ type: 'image', url: event.snapshot_url })}
        >
          {(event.thumbnail_url || event.snapshot_url) && !imageError ? (
            <img
              src={event.thumbnail_url || event.snapshot_url || undefined}
              alt="Event snapshot"
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Camera size={32} className="text-text-muted" />
            </div>
          )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
          {event.video_url ? (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Play size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMedia({ type: 'video', url: event.video_url! });
              }}
            >
              Play Video
            </Button>
          ) : (
            <Button 
              variant="outline" 
              size="sm" 
              leftIcon={<Eye size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/events/${event.event_id}/view`);
              }}
            >
              View
            </Button>
          )}
        </div>

        {/* Match Score Badge */}
        {event.match_score > 0 && (
          <div className="absolute top-2 right-2">
            {getMatchScoreBadge(event.match_score)}
          </div>
        )}

        {/* AI Badge */}
        {event.ai_description && (
          <div className="absolute top-2 left-2">
            <Badge variant="info" size="sm">
              <Sparkles size={12} className="mr-1" />
              AI
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <RegisteredPeopleLinks people={event.registered_persons || event.ai_metadata?.registered_persons} />
        {/* AI Description */}
        {event.ai_description && (
          <p className="text-sm text-text-primary line-clamp-2">
            {event.ai_description}
          </p>
        )}

        {/* AI Metadata Tags */}
        {event.ai_metadata && (
          <div className="flex flex-wrap gap-1">
            {event.ai_metadata.objects?.slice(0, 4).map((obj, i) => (
              <Badge key={i} variant="info" size="sm">{obj}</Badge>
            ))}
            {event.ai_metadata.text_detected?.slice(0, 2).map((text, i) => (
              <Badge key={`text-${i}`} variant="warning" size="sm">{text}</Badge>
            ))}
          </div>
        )}

        {/* Event Info */}
        <div className="flex items-center gap-4 text-xs text-text-muted">
          <div className="flex items-center gap-1">
            <Camera size={12} />
            <span>{event.camera_name || 'Unknown Camera'}</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin size={12} />
            <span>{event.location_name || 'Unknown Location'}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-surface-tertiary">
          <span className="text-xs text-text-muted">{formatDate(event.created_date, event.location_id)}</span>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Eye size={14} />}
            onClick={() => navigate(`/events/${event.event_id}/view`)}
          >
            Details
          </Button>
        </div>
      </div>
    </Card>
    );
  };

  // Visitor Log Card Component
  const VisitorLogCard: React.FC<{ log: VisitorLogResult }> = ({ log }) => {
    const [imageError, setImageError] = React.useState(false);
    
    return (
      <Card padding="none" className="overflow-hidden hover:shadow-lg transition-shadow">
        {/* Header Badge */}
        <div className="bg-purple-500/10 px-4 py-2 flex items-center justify-between border-b border-surface-tertiary">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-purple-500" />
            <span className="text-sm font-medium text-purple-500">VISITOR LOG</span>
          </div>
          <span className="text-xs text-text-muted">{formatTime(log.log_capture_time)}</span>
        </div>

        {/* Photo */}
        <div 
          className="relative aspect-video bg-surface-secondary cursor-pointer group"
          onClick={() => {
            if (log.image_url && !imageError) {
              setSelectedMedia({ type: 'image', url: log.image_url });
            } else {
              navigate(`/entry-exit/${log.log_id}`);
            }
          }}
        >
          {log.image_url && !imageError ? (
            <img
              src={log.image_url}
              alt="Visitor"
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <User size={32} className="text-text-muted" />
            </div>
          )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
          <Button 
            variant="outline" 
            size="sm" 
            leftIcon={<Eye size={16} />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/entry-exit/${log.log_id}`);
            }}
          >
            View Details
          </Button>
        </div>

        {/* Match Score Badge */}
        {log.match_score > 0 && (
          <div className="absolute top-2 right-2">
            {getMatchScoreBadge(log.match_score)}
          </div>
        )}

        {/* Access Type Badge */}
        <div className="absolute top-2 left-2">
          <Badge 
            variant={log.log_access_type === 'ENTRY' ? 'success' : 'warning'} 
            size="sm"
          >
            {log.log_access_type === 'ENTRY' ? (
              <><ArrowRight size={12} className="mr-1" /> ENTRY</>
            ) : (
              <><ArrowLeft size={12} className="mr-1" /> EXIT</>
            )}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Person Info */}
        <div>
          <p className="font-medium text-text-primary">
            {log.driver_name || 'Unknown Person'}
            {log.visitor_company && (
              <span className="text-text-muted font-normal"> ({log.visitor_company})</span>
            )}
          </p>
          <p className="text-xs text-text-muted capitalize">{log.log_operator_type?.toLowerCase()}</p>
        </div>

        {/* Vehicle Info */}
        {(log.vehicle_type || log.license_plate) && (
          <div className="flex items-center gap-2 text-sm text-text-secondary">
            <Truck size={14} />
            <span>
              {[log.vehicle_color, log.vehicle_type].filter(Boolean).join(' ')}
              {log.license_plate && <span className="font-mono ml-2">• {log.license_plate}</span>}
            </span>
          </div>
        )}

        {/* Truck/Trailer Numbers */}
        {(log.truck_number || log.trailer_number) && (
          <div className="flex flex-wrap gap-2">
            {log.truck_number && (
              <Badge variant="info" size="sm">Truck #{log.truck_number}</Badge>
            )}
            {log.trailer_number && (
              <Badge variant="info" size="sm">Trailer #{log.trailer_number}</Badge>
            )}
          </div>
        )}

        {/* Purpose */}
        {log.visitor_purpose && (
          <p className="text-xs text-text-muted line-clamp-1">
            Purpose: {log.visitor_purpose}
          </p>
        )}

        {/* Location */}
        <div className="flex items-center gap-1 text-xs text-text-muted">
          <MapPin size={12} />
          <span>{log.location_name || 'Unknown Location'}</span>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-surface-tertiary">
          <span className="text-xs text-text-muted">{formatDate(log.created_date)}</span>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Eye size={14} />}
            onClick={() => navigate(`/entry-exit/${log.log_id}`)}
          >
            Details
          </Button>
        </div>
      </div>
    </Card>
    );
  };

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 shrink-0 bg-accent-primary/10 border border-accent-primary/20 rounded-xl flex items-center justify-center">
                <Sparkles size={20} className="text-accent-primary" />
              </div>
              <div>
                <h1 className="page-title">AI Search</h1>
                <p className="page-description">Search events & visitors using natural language</p>
              </div>
            </div>
          </div>
          <Button
            variant={showFilters ? 'primary' : 'outline'}
            leftIcon={<Filter size={18} />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
        </div>

        {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}

        {/* Current Location Indicator */}
        {filterLocation !== 'all' && (
          <div className="flex items-center gap-2 px-4 py-2 bg-accent-primary/10 rounded-lg border border-accent-primary/20">
            <MapPin size={16} className="text-accent-primary" />
            <span className="text-sm text-text-secondary">
              Searching in: <strong className="text-text-primary">{currentLocationName}</strong>
            </span>
            <button 
              onClick={() => setFilterLocation('all')}
              className="ml-auto text-xs text-text-muted hover:text-text-primary"
            >
              Search all locations
            </button>
          </div>
        )}

        {/* Search Bar */}
        <Card padding="lg">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                aria-label="Search events and visitor logs"
                placeholder={`Search ${filterLocation !== 'all' ? currentLocationName : 'all locations'} by registered name, description, plate or date`}
                className="w-full pl-12 pr-4 py-3 bg-surface-secondary border border-surface-tertiary rounded-button text-text-primary placeholder-text-muted focus:outline-none focus:border-accent-primary text-lg"
              />
            </div>
            <Button
              variant="primary"
              size="lg"
              leftIcon={<Sparkles size={18} />}
              onClick={() => handleSearch()}
              isLoading={isLoading}
            >
              Search
            </Button>
          </div>

          {/* Quick Filters */}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeEvents}
                onChange={(e) => setIncludeEvents(e.target.checked)}
                className="rounded border-surface-tertiary"
              />
              <Video size={16} className="text-blue-500" />
              <span className="text-sm text-text-secondary">Camera Events</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeVisitorLogs}
                onChange={(e) => setIncludeVisitorLogs(e.target.checked)}
                className="rounded border-surface-tertiary"
              />
              <Users size={16} className="text-purple-500" />
              <span className="text-sm text-text-secondary">Visitor Logs</span>
            </label>
            <div className="flex-1" />
            <span className="text-xs text-text-muted">
              Tip: Search a registered person's name, or add "today" or "yesterday".
            </span>
          </div>

          {/* Suggestions */}
          {suggestions && !results.length && (
            <div className="mt-4 space-y-3">
              {suggestions.recent_searches?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <Clock size={16} className="text-text-muted" />
                  <span className="text-sm text-text-muted">Recent:</span>
                  {suggestions.recent_searches.slice(0, 5).map((term, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestionClick(term)}
                      className="px-3 py-1 text-sm bg-surface-secondary hover:bg-surface-hover rounded-full text-text-secondary transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              )}
              {suggestions.popular_searches?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <TrendingUp size={16} className="text-text-muted" />
                  <span className="text-sm text-text-muted">Popular:</span>
                  {suggestions.popular_searches.slice(0, 5).map((term, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestionClick(term)}
                      className="px-3 py-1 text-sm bg-surface-secondary hover:bg-surface-hover rounded-full text-text-secondary transition-colors"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="space-y-4">
            {/* Location & Date Filters */}
            <Card padding="md">
              <div className="flex items-center gap-2 mb-4">
                <Calendar size={18} className="text-accent-primary" />
                <h3 className="font-semibold text-text-primary">Location & Date Filters</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Select
                  options={locationOptions}
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  fullWidth
                />
                <Input
                  type="date"
                  placeholder="Start Date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                />
                <Input
                  type="date"
                  placeholder="End Date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                />
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Person Filters */}
              <Card padding="md">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <User size={18} className="text-accent-primary" />
                    <h3 className="font-semibold text-text-primary">Person Search (Events Only)</h3>
                  </div>
                  <Button variant="primary" size="sm" onClick={handlePersonSearch} isLoading={isLoading}>
                    Search
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Select
                    options={genderOptions}
                    value={personFilters.gender}
                    onChange={(e) => setPersonFilters({ ...personFilters, gender: e.target.value })}
                    fullWidth
                  />
                  <Select
                    options={clothingColorOptions}
                    value={personFilters.clothing_color}
                    onChange={(e) => setPersonFilters({ ...personFilters, clothing_color: e.target.value })}
                    fullWidth
                  />
                  <Select
                    options={clothingTypeOptions}
                    value={personFilters.clothing_type}
                    onChange={(e) => setPersonFilters({ ...personFilters, clothing_type: e.target.value })}
                    fullWidth
                  />
                </div>
              </Card>

              {/* Vehicle Filters */}
              <Card padding="md">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Truck size={18} className="text-accent-primary" />
                    <h3 className="font-semibold text-text-primary">Vehicle Search (Events Only)</h3>
                  </div>
                  <Button variant="primary" size="sm" onClick={handleVehicleSearch} isLoading={isLoading}>
                    Search
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    options={vehicleTypeOptions}
                    value={vehicleFilters.vehicle_type}
                    onChange={(e) => setVehicleFilters({ ...vehicleFilters, vehicle_type: e.target.value })}
                    fullWidth
                  />
                  <Select
                    options={vehicleColorOptions}
                    value={vehicleFilters.vehicle_color}
                    onChange={(e) => setVehicleFilters({ ...vehicleFilters, vehicle_color: e.target.value })}
                    fullWidth
                  />
                  <Input
                    placeholder="Unit Number"
                    value={vehicleFilters.unit_number}
                    onChange={(e) => setVehicleFilters({ ...vehicleFilters, unit_number: e.target.value })}
                    fullWidth
                  />
                  <Input
                    placeholder="License Plate"
                    value={vehicleFilters.license_plate}
                    onChange={(e) => setVehicleFilters({ ...vehicleFilters, license_plate: e.target.value })}
                    fullWidth
                  />
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Results */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loading size="lg" text="Searching events & visitors..." />
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-4">
            {/* Results Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <p className="text-text-muted">
                  Found <span className="font-semibold text-text-primary">{searchResponse?.total || results.length}</span> results
                </p>
                {searchResponse && (
                  <div className="flex items-center gap-3 text-sm">
                    {searchResponse.events_count > 0 && (
                      <span className="flex items-center gap-1 text-blue-500">
                        <Video size={14} /> {searchResponse.events_count} events
                      </span>
                    )}
                    {searchResponse.visitor_logs_count > 0 && (
                      <span className="flex items-center gap-1 text-purple-500">
                        <Users size={14} /> {searchResponse.visitor_logs_count} visitor logs
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" leftIcon={<X size={16} />} onClick={clearResults}>
                Clear Results
              </Button>
            </div>

            {/* Search Info */}
            {searchResponse?.date_filter && (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Calendar size={14} />
                <span>Date filter: <strong>{searchResponse.date_filter}</strong></span>
                {searchResponse.date_range && (
                  <span className="text-xs">
                    ({searchResponse.date_range.start} to {searchResponse.date_range.end})
                  </span>
                )}
              </div>
            )}

            {/* Results Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.map((result) => (
                isEventResult(result) 
                  ? <EventCard key={`e-${result.event_id}`} event={result} />
                  : isVisitorLogResult(result) 
                    ? <VisitorLogCard key={`v-${result.log_id}`} log={result} />
                    : null
              ))}
            </div>
            {searchResponse?.has_more && activeSearch && <div className="flex flex-col items-center gap-2">
              <p className="text-xs text-text-muted">Showing {results.length} of {searchResponse.total} results</p>
              <Button variant="outline" isLoading={isLoadingMore} onClick={loadMore}>Load more results</Button>
            </div>}
          </div>
        ) : searchQuery && !isLoading ? (
          <Card padding="lg">
            <div className="text-center py-12">
              <Search size={48} className="text-text-muted mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">No Results Found</h3>
              <p className="text-text-muted">Try a different search term or adjust your filters.</p>
            </div>
          </Card>
        ) : (
          <Card padding="lg">
            <div className="text-center py-12">
              <Sparkles size={48} className="text-purple-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-text-primary mb-2">AI-Powered Unified Search</h3>
              <p className="text-text-muted mb-4">
                Search across camera events and visitor logs using natural language. Try:
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {['red truck', 'today ABC-1234', 'John Smith', 'yesterday forklift', 'Acme Corp'].map((term) => (
                  <button
                    key={term}
                    onClick={() => handleSuggestionClick(term)}
                    className="px-4 py-2 bg-surface-secondary hover:bg-surface-hover rounded-lg text-text-secondary transition-colors"
                  >
                    "{term}"
                  </button>
                ))}
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Media Modal */}
      {selectedMedia && selectedMedia.url && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setSelectedMedia(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
            {selectedMedia.type === 'video' ? (
              <video
                src={selectedMedia.url}
                controls
                autoPlay
                className="w-full max-h-[80vh] rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={selectedMedia.url}
                alt="Media"
                className="w-full max-h-[80vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  // If image fails to load, close the modal
                  setSelectedMedia(null);
                }}
              />
            )}
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default AISearchPage;
